import { readDatabaseFailure } from "./constraints.js";
import { errorCatalog, errorDefinition } from "./catalog.js";
/**
 * خطأ واحد لكل النظام، **يستحيل بناؤه ناقصاً**.
 *
 * تُبنى من الكود وحده: الحالة ومفتاح الرسالة يأتيان من الكتالوج، فلا يوجد مسار
 * يرمي خطأً بلا رسالة يفهمها المستخدم. وهذا هو الفرق عن v2، حيث كان المرشِّح
 * يمرّر ما رماه الرامي كما هو — فكانت أخطاء التحقّق وحدود الطلبات والمسارات
 * غير المسجّلة تصل جميعها بلا مفتاح رسالة، فتُعرَض كبانر عام.
 */
export class AppError extends Error {
    code;
    status;
    messageKey;
    fields;
    context;
    constructor(code, options = {}) {
        const definition = errorCatalog[code];
        super(`${code}: ${definition.messageKey}`, { cause: options.cause });
        this.name = "AppError";
        this.code = code;
        this.status = definition.status;
        this.messageKey = definition.messageKey;
        if (options.fields)
            this.fields = options.fields;
        if (options.context)
            this.context = options.context;
        /**
         * **لا رسالة تخرج بفراغٍ لم يُملأ.**
         *
         * هذا عطبُ مبرمج لا نتيجةُ عمل، فيسقط هنا وقت الكتابة — لا يظهر للمستخدم
         * نصّاً فيه `{limitMb}` بين قوسين.
         */
        for (const key of errorDefinition(code).contextKeys ?? []) {
            if (options.context?.[key] === undefined) {
                throw new Error(`AppError ${code} requires context.${key} — its message has a {${key}} placeholder.`);
            }
        }
    }
    /** هل تُرفع هذه كحادثة في صحة النظام؟ الأخطاء عندنا فقط — لا نتائج العمل الطبيعية. */
    get raisesIncident() {
        return errorDefinition(this.code).incident === true;
    }
    /** الجسم الذي يُرسَل على السلك. `correlationId` يحقنه المرشِّح من سياق الطلب. */
    toBody(correlationId) {
        return {
            code: this.code,
            messageKey: this.messageKey,
            correlationId,
            ...(this.fields ? { fields: this.fields } : {}),
            ...(this.context ? { context: this.context } : {}),
        };
    }
}
export function isAppError(value) {
    return value instanceof AppError;
}
/**
 * الحارس الذي يجعل الصمت مستحيلاً.
 *
 * أي شيء يُرمى — من مكتبة، من قاعدة البيانات، `throw new Error` عارٍ — يخرج من
 * هنا خطأً كامل الشكل. المرشِّح **يوحّد، ولا يمرّر**.
 */
export function normalizeError(thrown) {
    if (isAppError(thrown))
        return thrown;
    /**
     * **وخطأُ القاعدة يُترجَم قبل أن يصير «داخلياً»** — فقيدُها قاعدةُ عمل.
     *
     * وهذا الموضعُ هو الوحيد: يقرؤه المعترِض ومرشِّحُ الخادم كلاهما، **فلا
     * ترجمتان تفترقان**. وبلا هذا السطر كان خرقُ أي فهرسٍ فريد يصل المستخدمَ
     * `500` بلا رسالةٍ ولا كودٍ يقتبسه — وهو ما يمنعه المبدأُ نصّاً.
     */
    const database = readDatabaseFailure(thrown);
    if (database) {
        return new AppError(database.code, {
            cause: thrown,
            context: {
                ...(database.entity === null ? {} : { entity: database.entity }),
                ...(database.constraint === null ? {} : { constraint: database.constraint }),
                ...(database.sqlState === null ? {} : { sqlState: database.sqlState }),
                ...(database.because === null ? {} : { because: database.because }),
            },
        });
    }
    return new AppError("INTERNAL", {
        cause: thrown,
        context: thrown instanceof Error ? { originalMessage: thrown.message } : {},
    });
}
/**
 * فحص وقت التشغيل خارج الإنتاج: يرمي إن خرج ردّ خطأ ناقصاً.
 *
 * فتُكتشف الثغرة عندنا بصوت عالٍ، لا عند العميل بصمت.
 */
export function assertCompleteErrorBody(body) {
    const missing = [];
    if (!body.code)
        missing.push("code");
    if (!body.messageKey)
        missing.push("messageKey");
    if (!body.correlationId)
        missing.push("correlationId");
    if (missing.length > 0) {
        throw new Error(`Error contract violated: response missing ${missing.join(", ")}. ` +
            `Every error leaving this system carries a code, a message key and a correlation id.`);
    }
}
//# sourceMappingURL=app-error.js.map